import { theme as coreTheme, CoreTheme } from '@kizi/core-components';
import inputs from './variants/inputs';
import buttons from './variants/buttons';
import divs from './variants/divs';
import headings from './variants/headings';
import lists from './variants/lists';
import paragraphs from './variants/paragraphs';
import svgs from './variants/svgs';
import spans from './variants/spans';
import selects from './variants/selects';
import labels from './variants/labels';
import tables from './variants/tables';
import anchors from './variants/anchors';
import tableRows from './variants/tableRows';
import tableCells from './variants/tableCells';

//this is just to show you how to add variant groups
export type ComparatorVariantGroups = 'steps' | 'timeline';

type ComparatorTheme = CoreTheme<string> & {
  gradients: {
    [props: string]: any;
  };
};

export const baseSize = 16;

const baseColors = {
  main: ['#FFFFFF', '#007BFF', '#2B2F3B', '#F70844', '#EA5D5D', '#727888'],
  second: ['#D9DADE', '#F0F0EF', '#F2F2F0', '#F1FEFA', '#042F21'],
  supplementary: [
    '#FF806E',
    '#FFA617',
    '#00DD83',
    '#B061F7',
    '#00D1DB',
    '#ED0164',
    '#C4C4C4',
  ],
  dark: ['#540021', '#492300', '#00443F', '#4F055E', '#2B2F3B', '#5C0020'],
};

//this is just an example to show you how to copy all the keys in coreTheme.colors and redefine some keys
const colors = {
  ...baseColors,
  additionalInfo: baseColors.second[0],
  advice: baseColors.supplementary[1],
  alert: baseColors.supplementary[5],
  background: baseColors.main[0],
  danger: baseColors.supplementary[5],
  disabled: '#9B9B9B',
  disabledLight: baseColors.second[1],
  divider: baseColors.second[1],
  error: baseColors.supplementary[5],
  heading: baseColors.main[2],
  info: baseColors.main[1],
  placeholder: '#9B9B9B',
  primary: baseColors.main[1],
  rating: baseColors.main[2],
  secondary: baseColors.supplementary[4],
  success: baseColors.supplementary[2],
  tertiary: '',
  text: baseColors.main[2],
  textLight: baseColors.main[5],
  textAdvice: baseColors.dark[1],
  warning: baseColors.supplementary[1],
  white: baseColors.main[0],
  lightMain1: `${baseColors.main[1]}4D`,
  comparatorBG: '#F8F8F7',
};

const gradients = {
  primary: {
    radial: `radial-gradient(50% 109.37% at 50% 50%, #0046FF 0%, #0008E2 100%)`,
    right: `linear-gradient(270deg, #0046FF 0%, #0008E2 100%);`,
    left: `linear-gradient(90deg, #0046FF -1.75%, #0008E2 100%);`,
    down: `linear-gradient(180deg, #0008E2 0%, #0046FF 100%);`,
    up: `linear-gradient(153.44deg, #0046FF 16.67%, #0008E2 83.33%);`,
    angleUp: `linear-gradient(45deg, #0008E2 0%, #0046FF 100%);`,
    angleDown: `linear-gradient(315deg, #0046FF 0%, #0008E2 100%);`,
  },
  secondary: {
    radial: `radial-gradient(50% 109.37% at 50% 50%, #0008E2 0%, #00047A 100%);`,
    right: `linear-gradient(270deg, #0008E2 0%, #00047A 100%);`,
    left: `linear-gradient(90deg, #0008E2 -1.75%, #00047A 100%);`,
    down: `linear-gradient(180deg, #00047A 0%, #0008E2 100%);`,
    up: `linear-gradient(153.44deg, #0008E2 16.67%, #00047A 83.33%);`,
    angleUp: `linear-gradient(251.98deg, #0008E2 0%, #00047A 100%);`,
    angleDown: `linear-gradient(135deg, #00047A 0%, #0008E2 100%);`,
  },
  tertiary: {
    radial: `radial-gradient(50% 109.37% at 50% 50%, #00047A 0%, #000347 100%);`,
    right: `linear-gradient(270deg, #00047A 0%, #000347 100%);`,
    left: `linear-gradient(90deg, #00047A -1.75%, #000347 100%);`,
    down: `linear-gradient(180deg, #000347 0%, #00047A 100%);`,
    up: `linear-gradient(153.44deg, #00047A 16.67%, #000347 83.33%);`,
    angleUp: `linear-gradient(45deg, #000347 0%, #00047A 100%);`,
    angleDown: `linear-gradient(135deg, #00047A 0%, #000347 100%);`,
  },
  quaternary: {
    radial: `radial-gradient(50% 109.37% at 50% 50%, #667085 0%, #475467 100%);`,
    right: `linear-gradient(90deg, #475467 0%, #667085 100%);`,
    left: `linear-gradient(45deg, #344054 0%, #475467 100%);`,
    down: `linear-gradient(45deg, #1D2939 0%, #475467 100%);`,
    up: `linear-gradient(63.44deg, #1D2939 16.72%, #475467 83.39%);`,
    angleUp: `linear-gradient(26.57deg, #1D2939 8.33%, #344054 91.67%);`,
    angleDown: `linear-gradient(45deg, #101828 0%, #475467 100%);`,
  },
};

export const breakpoints = ['425px', '768px', '1280px', '1440px', '2000px'];

export const fonts = {
  body: 'Gilroy, Helvetica, Arial, sans-serif',
  button: 'Gilroy, Helvetica, Arial, sans-serif',
  heading: 'Gilroy, Helvetica, Arial, sans-serif',
};

export const fontSizes = [11, 12, 14, 16, 18, 20, 36, 56].map(
  (px) => `${px ? px / baseSize : 0}rem`,
);

export const fontWeights = [400, 700];

export const lineHeights = ['1.2', '1.4', '1.5'];

export const mediaQueries = {};

export const radii = ['0px', '4px', '10px', '50px'];

export const shadows = {
  // 1: {
  //   boxShadow: `0px 1px 2px rgba(16, 24, 40, 0.05);`,
  // },
  // 2: {
  //   boxShadow: `0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06);`,
  // },
  // 3: {
  //   boxShadow: `0px 4px 8px -2px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.06);`,
  // },
  // 4: {
  //   boxShadow: `0px 12px 16px -4px rgba(16, 24, 40, 0.1), 0px 4px 6px -2px rgba(16, 24, 40, 0.05);`,
  // },
  // 5: {
  //   boxShadow: `0px 20px 24px -4px rgba(16, 24, 40, 0.1), 0px 8px 8px -4px rgba(16, 24, 40, 0.04);`,
  // },
  // 6: {
  //   boxShadow: `0px 24px 48px -12px rgba(16, 24, 40, 0.25);`,
  // },
  // 7: {
  //   boxShadow: `0px 32px 64px -12px rgba(16, 24, 40, 0.2);`,
  // },
  default: `0px 0px 20px rgba(0, 0, 0, 0.15);`,
};

export const space = [0, 5, 10, 15, 20, 25, 30, 50].map(
  (px) => `${px ? px / baseSize : 0}rem`,
);

export const zIndices = [0, 1, 2];

const themeOsSample: ComparatorTheme = {
  ...coreTheme,
  breakpoints,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  radii,
  shadows,
  space,
  gradients,
  variants: {
    anchors,
    buttons,
    divs,
    tables,
    tableRows,
    tableCells,
    headings,
    paragraphs,
    labels,
    lists,
    selects,
    svgs,
    inputs,
    // : {
    //   ...inputs,
    //   checkboxes: {
    //     main: {
    //       fontSize: 2,
    //       border: '1px solid',
    //       borderColor: 'second.0',
    //       borderRadius: '4px',
    //       '&:hover': {
    //         borderColor: 'main.1',
    //       },
    //       checked: {
    //         fontSize: 2,
    //         border: '2px solid',
    //         borderColor: 'main.1',
    //         borderRadius: '4px',
    //         '& input ~ span': {
    //           borderRadius: '0px',
    //         },
    //         '& input:checked ~ span svg': {
    //           fill: 'white',
    //         },
    //         '& input:checked ~ span': {
    //           bg: 'main.1',
    //         },
    //       },
    //       disabled: {
    //         fontSize: 2,
    //         border: '1px solid',
    //         borderColor: 'second.1',
    //         borderRadius: 1,
    //         '& input ~ span': {
    //           borderRadius: '2px',
    //         },
    //         '& input:checked ~ span svg': {
    //           fill: 'second.1',
    //         },
    //         background: '#F2F2F0',
    //       },
    //     },
    //   },
    // },
    spans,
    steps: {
      active: {
        bg: 'main.2',
        border: '1px solid main.2',
        borderRadius: 3,
        height: [20, 30],
        width: [20, 30],
      },
      activeText: {
        color: 'text',
        fontFamily: 'body',
        fontSize: [1, 3],
        lineHeight: 2,
        ml: [2, 3],
      },
      dash: {
        bg: 'second.1',
        height: '1px',
        mx: [1, 3],
        width: [15, 30],
      },
      done: {
        color: '#12DE9A',
        fontFamily: 'body',
        fontSize: [1, 3],
        lineHeight: 2,
        ml: [2, 3],
      },
      inactive: {
        bg: 'second.0',
        border: '1px solid second.1',
        borderRadius: 3,
        height: [20, 30],
        width: [20, 30],
      },
      inactiveText: {
        color: 'second.0',
        fontFamily: 'body',
        fontSize: [1, 3],
        lineHeight: 2,
        ml: [2, 3],
      },
      stepDone: {
        bg: '#12DE9A',
        border: '1px solid second.1',
        borderRadius: 3,
        height: [20, 30],
        width: [20, 30],
      },
      stepNumber: {
        color: 'white',
        fontFamily: ['body', 'heading'],
        fontSize: [1, 3],
        fontWeight: 1,
        lineHeight: 2,
      },
      verticalDash: {
        bg: 'second.1',
        height: [15, 30],
        my: [1, 3],
        ml: [2, 3],
        width: '1px',
      },
    },
    timeline: {
      dash: {
        bg: 'second.1',
        height: '100%',
        ml: 3,
        width: '1px',
      },
      dashWide: {
        bg: 'second.2',
        height: '100%',
        ml: '17px',
        width: '4px',
      },
      dashHorizontal: {
        position: 'absolute',
        top: 24,
        left: 4,
        bg: 'second.2',
        height: '2px',
        width: '90%',
      },
      item: {
        '&:not(:last-of-type)': {
          pb: 6,
        },
      },
      itemHorizontal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      marker: {
        bg: 'main.2',
        border: '1px solid main.2',
        borderRadius: 3,
        height: 30,
        width: 30,
      },

      markerNumber: {
        color: 'white',
        fontFamily: 'body',
        fontSize: 2,
        fontWeight: 1,
        lineHeight: 2,
      },
      textBlock: {
        ml: 3,
        '> p:first-of-type': {
          mt: 1,
        },
      },
      textBlockHorizontal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '>p:first-of-type': {
          fontSize: 4,
        },
        '>p:last-of-type': {
          fontSize: 1,
        },
      },
    },
  },
  global: {
    body: {
      fontFamily: fonts.body,
    },
  },
  zIndices,
};

export default themeOsSample;
